import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate, formatNumber, ratingColor, scoreColor } from "@/lib/format";
import { Star, ArrowSquareOut, Plus, Check } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { toast } from "sonner";

export default function AppDetail() {
  const { pkg } = useParams();
  const [data, setData] = useState(null);

  const load = () => api.get(`/apps/${pkg}`).then(r => setData(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, [pkg]);

  if (!data) return <div className="p-10">Loading…</div>;
  const { app, snapshots, score, lead } = data;

  const chartData = snapshots.map(s => ({
    date: new Date(s.timestamp).toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
    rating: s.rating,
    reviews: s.review_count,
  }));

  const addLead = async () => {
    try {
      await api.post("/leads", { package_name: pkg });
      toast.success("Lead added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-8 max-w-6xl">
      <div className="flex items-start gap-5">
        {app.icon && <img src={app.icon} alt="" className="w-20 h-20 rounded-md border border-border" />}
        <div className="flex-1 min-w-0">
          <div className="overline-label text-muted-foreground">{app.category}</div>
          <h1 className="font-heading font-black text-3xl mt-1">{app.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            <span className="font-mono">{app.package_name}</span>
            <span>·</span>
            <Link to={`/developers/${encodeURIComponent(app.developer_id || app.developer)}`}
                  className="hover:underline">{app.developer}</Link>
            {app.play_store_url && (
              <a href={app.play_store_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 hover:underline">
                <ArrowSquareOut size={12} /> Play Store
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className={`inline-flex items-center px-3 py-1.5 border rounded-sm font-mono font-semibold ${scoreColor(score.score)}`}
               data-testid="detail-score">
            Lead Score {score.score}
          </div>
          {lead ? (
            <div className="text-xs text-emerald-600 border border-emerald-500/30 px-3 py-1.5 rounded-sm flex items-center gap-1">
              <Check size={14} /> Already a Lead ({lead.status})
            </div>
          ) : (
            <button data-testid="btn-add-lead" onClick={addLead}
                    className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-sm flex items-center gap-1.5 hover:opacity-90">
              <Plus size={14} /> Add as Lead
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 grid-borders border-l border-t border-border">
        {[
          ["Rating", <span className={ratingColor(app.rating)}><Star size={14} weight="fill" className="inline mr-1" />{app.rating?.toFixed(1) || "—"}</span>],
          ["Reviews", formatNumber(app.review_count)],
          ["Installs", app.install_range],
          ["Version", app.version || "—"],
          ["First Seen", formatDate(app.first_seen)],
          ["Last Updated", app.app_updated ? formatDate(new Date(app.app_updated * 1000).toISOString()) : "—"],
        ].map(([label, val], i) => (
          <div key={i} className="p-4">
            <div className="overline-label text-muted-foreground">{label}</div>
            <div className="font-heading font-black text-lg mt-1">{val}</div>
          </div>
        ))}
      </div>

      <section>
        <h3 className="font-heading font-black text-xl mb-3">Rating History</h3>
        <div className="border border-border p-4 bg-card h-64">
          {chartData.length > 1 ? (
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="rating" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Not enough snapshots yet.</div>}
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-xl mb-3">Review Growth</h3>
        <div className="border border-border p-4 bg-card h-56">
          {chartData.length > 1 ? (
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="reviews" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Not enough snapshots yet.</div>}
        </div>
      </section>

      <section>
        <h3 className="font-heading font-black text-xl mb-3">Lead Score Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(score.breakdown).map(([k, v]) => (
            <div key={k} className="border border-border p-3">
              <div className="overline-label text-muted-foreground">{k.replace(/_/g, " ")}</div>
              <div className="font-heading font-black text-xl mt-1 font-mono">{v}</div>
            </div>
          ))}
        </div>
        {score.reasons?.length > 0 && (
          <div className="mt-4 border border-border p-4 bg-secondary/30">
            <div className="overline-label text-muted-foreground mb-2">Why this score</div>
            <ul className="text-sm space-y-1 list-disc pl-5">
              {score.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </section>

      {app.description && (
        <section>
          <h3 className="font-heading font-black text-xl mb-3">Description</h3>
          <div className="border border-border p-4 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
            {app.description}
          </div>
        </section>
      )}

      {app.screenshots?.length > 0 && (
        <section>
          <h3 className="font-heading font-black text-xl mb-3">Screenshots</h3>
          <div className="flex gap-3 overflow-x-auto">
            {app.screenshots.map((s, i) => (
              <img key={i} src={s} alt="" className="h-48 border border-border rounded-sm" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
